import React, { useState, useEffect } from "react";
import s from "./CreateOrder.module.css";
//Servicios
import ordersService from "../../../../api/delivery/ordersServices";
//assets
import { today } from "../../../../helpers/today";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import Swal from "sweetalert2";
//material
import Grid from "@mui/material/Grid"
import Button from "@mui/material/Button"
import Stepper from "@mui/material/Stepper"
import Step from "@mui/material/Step"
import StepButton from "@mui/material/StepButton"
import CircularProgress from "@mui/material/CircularProgress"
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { StepperOne, StepperTwo, ConfirmStepper } from "./Steppers";
// Utils
import useTranslationException from "../../../../helpers/exceptions";
import { useNotificationContext } from "../../../Notifications/CustomNotifications";

// ========================= | CREATE ORDER | =================================

export const CreateOrder = ({
  edit,
  setEdit,
  setPage,
  setItems,
  setTotalPages,
  sort,
}) => {
  const notification = useNotificationContext();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const showExpetion = useTranslationException(t, i18n);
  const { textColor } = useSelector((state) => state.local);
  const { activeOrganization } = useSelector((state) => state.gamma);

  const [loading, setLoading] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [providerSelect, setProviderSelect] = useState(
    edit.status ? edit.item.supplier : { businessName: t("provider") },
  );
  const [details, setDetails] = useState([]);
  const [form, setForm] = useState({
    id: edit.status ? edit.item.id : 0,
    supplierId: edit.status ? edit.item.supplier.id : 0,
    dateCreate: edit.status ? edit.item.dateCreate : today(),
    observations: edit.status ? edit.item.observations : "",
    status: edit.status ? edit.item.status : "En Proceso",
    organizationId: edit.status
      ? edit.item.organizationId
      : activeOrganization?.id,
    detailOrdersList: edit.status ? edit.item.detailOrdersList : [],
  });

  useEffect(() => {
    if (edit.status) {
      let newDetails = form.detailOrdersList.map((item) => ({
        id: item.id,
        gameId: item.game.id,
        amount: item.amount,
        nroCouponFrom: item.nroCouponFrom,
        nroCouponTo: item.nroCouponTo,
        raffleId: item.raffle.id,
      }));
      setDetails(form?.detailOrdersList);
      setForm((prev) => ({ ...prev, ...form, detailOrdersList: newDetails }));
    }
  }, []);

  const handleSave = () => {
    setLoading(true);
    Swal.fire({
      title: t("orderSave"),
      text: t("orderSaveConfirm"),
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#00A36C",
      cancelButtonColor: "#d33",
      confirmButtonText: t("confirm"),
      cancelButtonText: t("cancel"),
    })
      .then((result) => {
        if (result.isConfirmed) {
          ordersService
            .editOrder(form)
            .then(() => {
              ordersService
                .getAllOrders(1, 10, sort)
                .then((res) => {
                  setTotalPages(res.totalPages);
                  setPage(1);
                  setItems(res.content);
                })
                .then(() => setLoading(false))
                .then(() => navigate("../ordenes-pedido"))
                .catch((error) => {
                  notification.error("Error ep", error.message);
                });
            })
            .catch((e) => {
              showExpetion.showErrorModalForServiceException(
                e,
                { icon: "warning" },
                t("anErrorOccurred,TryAgain"),
                t("sorryErrorOcurred"),
              );
              setLoading(false);
            });
        } else {
          setLoading(false);
        }
      })
      .catch((e) => {
        showExpetion.showErrorModalForServiceException(
          e,
          { icon: "warning" },
          t("anErrorOccurred,TryAgain"),
          t("sorryErrorOcurred"),
        );
        setLoading(false);
      });
  };

  const handleCreate = () => {
    setLoading(true);
    Swal.fire({
      title: t("orderSave"),
      text: t("orderSaveConfirm"),
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#00A36C",
      cancelButtonColor: "#d33",
      confirmButtonText: t("confirm"),
      cancelButtonText: t("cancel"),
    })
      .then((result) => {
        if (result.isConfirmed) {
          ordersService.createOrder(form)
            .then(() => navigate("../ordenes-pedido"))
            .then(() => {
              ordersService
                .getAllOrders(1, 10, sort)
                .then((res) => {
                  setTotalPages(res.totalPages);
                  setPage(1);
                  setItems(res.content);
                })
                .then(() => setLoading(false))
                .catch((error) => {
                  notification.error("Error ep", error.message);
                });
            });
          setLoading(false);
        }
      })
      .catch((e) => {
        showExpetion.showErrorModalForServiceException(
          e,
          { icon: "warning" },
          t("anErrorOccurred,TryAgain"),
          t("sorryErrorOcurred"),
        );
        setLoading(false);
      });
  };

  const handleBack = () => {
    Swal.fire({
      title: t("formExitTitle"),
      text: t("formExitHtml"),
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#00A36C",
      cancelButtonColor: "#d33",
      confirmButtonText: t("accept"),
      cancelButtonText: t("cancel"),
    }).then((result) => {
      if (result.isConfirmed) {
        navigate("../ordenes-pedido")
        ordersService
          .getAllOrders(1, 10, sort)
          .then((res) => {
            setEdit({
              status: false,
              item: {},
            });
            setTotalPages(res.totalPages);
            setPage(1);
            setItems(res.content);
          })
          .catch((error) => {
            notification.error("Error ep", error.message);
          });
      }
    });
  };

  return (
    <Grid container className={s.container}>
      <Grid item xs={10} className={s.titleContainer}>
        <ArrowBackIcon
          sx={{ color: "primary.main", fontSize: "2rem" }}
          onClick={handleBack}
          className={s.arrow}
        />
        <h4>
          {edit.status
            ? `${t("editOrder")}: ${form.id.toString().padStart(5, "0")}`
            : t("initOrder")}
        </h4>
      </Grid>

      <Grid container item xs={10} className={s.stepperContainer}>
        <Stepper
          alternativeLabel
          activeStep={activeStep}
          sx={{ width: "100%" }}
        >
          <Step>
            <StepButton color="inherit">{t("initOrder")}</StepButton>
          </Step>
          <Step>
            <StepButton color="inherit">{t("details")}</StepButton>
          </Step>
          <Step>
            <StepButton color="inherit">{t("confirm")}</StepButton>
          </Step>
        </Stepper>
      </Grid>

      <Grid container item xs={10} className={s.data}>
        {activeStep === 0 ? (
          <StepperOne
            form={form}
            setForm={setForm}
            providerSelect={providerSelect}
            setProviderSelect={setProviderSelect}
          />
        ) : activeStep === 1 ? (
          <StepperTwo
            setForm={setForm}
            form={form}
            edit={edit}
            raffles={details}
            setRaffles={setDetails}
          />
        ) : (
          <ConfirmStepper form={form} raffles={details} />
        )}
        <Grid item xs={12} className={s.stepperButtons}>
          <Button
            variant="contained"
            onClick={() => setActiveStep(activeStep === 0 ? 0 : activeStep - 1)}
            className={s.button}
            sx={{ backgroundColor: "primary.main", color: textColor }}
            disabled={activeStep === 0}
          >
            {t("back")}
          </Button>
          <Button
            variant="contained"
            disabled={
              !form.supplierId ||
              loading ||
              (activeStep === 1 && !form.detailOrdersList.length)
            }
            onClick={() => {
              activeStep === 2
                ? edit.status
                  ? handleSave()
                  : handleCreate()
                : setActiveStep((prev) => prev + 1);
            }}
            className={s.button}
            sx={{ backgroundColor: "primary.main", color: textColor }}
          >
            {loading ? (
              <CircularProgress />
            ) : activeStep === 2 ? (
              t("confirm")
            ) : (
              t("next")
            )}
          </Button>
        </Grid>
      </Grid>
    </Grid>
  );
};
